import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import PublicHeader from "@/components/PublicHeader";
import SiteFooter from "@/components/SiteFooter";
import { AtsLocked } from "@/pages/ResumeCheck";
import { useAuth } from "@/context/AuthContext";
import { useSeo, ORG_LD, crumbs } from "@/lib/seo";

const FAQ = [
  ["What is an ATS?", "An applicant tracking system is the software most employers use to receive, read and filter resumes before a recruiter sees them."],
  ["How does the JobPedestal ATS check work?", "It reads your file the way common ATS software does, then checks readable text, contact details, section headings, dates, layout, measurable results and the keywords used by open roles that match your background."],
  ["Who can use it?", "The ATS check is included with every JobPedestal paid plan, together with job matching and the \"Fix my resume\" AI rewrite."],
  ["Do you keep my resume?", "Your resume is stored encrypted in your account so your job matches can use it, and it is never shown to recruiters."],
  ["Will passing guarantee an interview?", "No. Every employer sets up its ATS differently. The check shows the most common reasons resumes get filtered so you can fix them."],
];

export default function ResumeAts() {
  const { user } = useAuth();
  useSeo({ title: "ATS resume checker — will your resume pass?", path: "/resume-ats-checker",
    description: "Find out whether applicant tracking systems can read your resume, which keywords you're missing, and which US & Canada jobs fit you. Included with JobPedestal paid plans.",
    jsonLd: [ORG_LD, crumbs([["Home", "/"], ["ATS resume checker", "/resume-ats-checker"]]),
      { "@type": "FAQPage", mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) }] });
  const to = user ? "/app/resume" : "/register?next=/app/resume";
  return (
    <div className="min-h-screen" data-testid="ats-public-page">
      <PublicHeader />
      <main className="mx-auto max-w-5xl px-4 sm:px-8">
        <section className="py-14 sm:py-20">
          <p className="eyebrow mb-3">ATS resume checker · US & Canada</p>
          <h1 className="max-w-4xl text-4xl font-extrabold sm:text-5xl lg:text-6xl">Will your resume pass the ATS?</h1>
          <p className="mt-5 max-w-2xl text-base text-slate-600 md:text-lg">Most employers filter resumes with software before a person reads them. Subscribers upload a resume and see their ATS score, exactly what to fix, and the keywords open roles in the US and Canada expect.</p>
        </section>
        <AtsLocked cta={user ? "Open the ATS check" : "Create an account and choose a plan"} to={to} />
        <section className="py-16" data-testid="ats-public-faq">
          <h2 className="text-2xl font-bold">Questions about ATS resume checks</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">{FAQ.map(([q, a]) => (
            <div key={q} className="rounded-xl border bg-white p-5"><h3 className="font-semibold">{q}</h3><p className="mt-2 text-sm text-slate-600">{a}</p></div>))}</div>
          <Button size="lg" className="mt-10 rounded-full" asChild data-testid="ats-public-bottom-cta"><Link to="/pricing">See plans <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
